import React, { useState, useEffect, useRef } from "react";
import {
    customerLogin,
    sendWhatsappVerificationCode
} from "../../../services/auth";
import {
    Alert,
    CircularProgress,
    LinearProgress,
    Snackbar
} from "@mui/material";
import { useAuth } from "../../../context/AuthContext";
import "../modal-base.css";
import DynamicDropdown from "../../Selector/Selector";
import { getDropdownCountryOptions } from "../../../utils/countryOptions";

export default function LoginModal({ onClose, onLoginSuccess }) {
    const countryOptions = getDropdownCountryOptions();

    const { setUserToken, setUserData } = useAuth();

    const [loginData, setLoginData] = useState({
        countryCode: "972", // default to Palestine
        phone: "",
        code: ""
    });

    const [verificationCodeSent, setVerificationCodeSent] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isSendingVerificationCode, setIsSendingVerificationCode] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarContent, setSnackbarContent] = useState({ message: "", type: "" });
    const [isTokenSet, setIsTokenSet] = useState(false);

    const phoneInputRef = useRef(null);

    useEffect(() => {
        if (phoneInputRef.current) {
            phoneInputRef.current.focus();
        }
    }, []);

    const handleChange = (field, value) =>
        setLoginData((prev) => ({ ...prev, [field]: value }));

    const validatePhone = (phone) => /^\d{6,15}$/.test(phone);

    const handleSendVerificationCode = async () => {
        if (!validatePhone(loginData.phone)) {
            setSnackbarContent({
                message: "يرجى إدخال رقم هاتف صحيح",
                type: "error"
            });
            setSnackbarOpen(true);
            return;
        }

        try {
            setIsSendingVerificationCode(true);
            await sendWhatsappVerificationCode(loginData.countryCode + loginData.phone);
            setSnackbarContent({
                message: "تم ارسال رمز التحقق الى تطبيق واتساب",
                type: "success"
            });
            setVerificationCodeSent(true);
        } catch (err) {
            setSnackbarContent({
                message: "فشل إرسال رمز التحقق. يرجى التأكد من الرقم.",
                type: "error"
            });
        } finally {
            setSnackbarOpen(true);
            setIsSendingVerificationCode(false);
        }
    };

    const handleLogin = async () => {
        if (!validatePhone(loginData.phone) || !loginData.code) {
            setSnackbarContent({
                message: "يرجى إدخال رقم الهاتف ورمز التحقق بشكل صحيح",
                type: "error"
            });
            setSnackbarOpen(true);
            return;
        }

        try {
            setIsLoading(true);
            const fullPhone = loginData.countryCode + loginData.phone;
            const res = await customerLogin(fullPhone, loginData.code);

            const userData = res.data.customer;
            userData.phone = fullPhone;

            setUserData(userData);
            setUserToken(res.data.accessToken);

            setIsTokenSet(true);
        } catch (err) {
            setSnackbarContent({
                message: "فشل تأكيد الرمز. يرجى التحقق من البيانات.",
                type: "error"
            });
            setSnackbarOpen(true);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (verificationCodeSent) {
            handleLogin();
        } else {
            handleSendVerificationCode();
        }
    };

    useEffect(() => {
        if (isTokenSet) {
            onLoginSuccess();
            onClose();
        }
    }, [isTokenSet, onLoginSuccess, onClose]);

    return (
        <div className="modal-backdrop">
            <div className="modal-box" style={{ width: "400px", maxHeight: "90vh", overflowY: "auto" }} dir="rtl">
                <button className="modal-close" onClick={onClose}>×</button>

                {isLoading ? (
                    <div style={{ height: "300px", display: "flex", justifyContent: "center", alignItems: "center" }}>
                        <CircularProgress />
                    </div>
                ) : (
                    <form style={{ padding: "20px" }} onSubmit={handleSubmit}>
                        <h2 style={{ textAlign: "center", marginBottom: "20px" }}>التحقق</h2>

                        <div className="form-group">
                            <label htmlFor="phone">رقم الهاتف</label>
                            <div style={{ display: "flex", gap: "5px" }}>
                                <input
                                    id="phone"
                                    type="text"
                                    inputMode="numeric"
                                    placeholder="أدخل رقم الهاتف"
                                    style={{ flex: 2 }}
                                    value={loginData.phone}
                                    onChange={(e) => {
                                        const digitsOnly = e.target.value.replace(/\D/g, "");
                                        handleChange("phone", digitsOnly);
                                    }}
                                    ref={phoneInputRef}
                                    required
                                />
                                <div style={{ flex: 1 }}>
                                    <DynamicDropdown
                                        label="رمز الدولة"
                                        value={loginData.countryCode}
                                        onChange={(val) => handleChange("countryCode", val)}
                                        options={countryOptions}
                                        sx={{ minWidth: 120, maxWidth: 150 }}
                                    />
                                </div>
                            </div>
                        </div>

                        {verificationCodeSent && (
                            <div className="form-group">
                                <label htmlFor="code">رمز التحقق</label>
                                <input
                                    id="code"
                                    type="text"
                                    inputMode="numeric"
                                    placeholder="أدخل رمز الدخول"
                                    value={loginData.code}
                                    onChange={(e) => {
                                        const digitsOnly = e.target.value.replace(/\D/g, "");
                                        handleChange("code", digitsOnly);
                                    }}
                                    required
                                />
                            </div>
                        )}

                        {isSendingVerificationCode && <LinearProgress color="primary" />}

                        <button
                            type="submit"
                            className={`btn filled ${isLoading || isSendingVerificationCode ? "disabled" : ""}`}
                            style={{ width: "100%", padding: "10px 0", fontSize: "15px", marginTop: "15px" }}
                            disabled={isLoading || isSendingVerificationCode}
                        >
                            {isSendingVerificationCode
                                ? "جاري الإرسال..."
                                : verificationCodeSent
                                    ? "التحقق من رمز التأكيد"
                                    : "ارسال رمز التأكيد"}
                        </button>

                        <Snackbar
                            open={snackbarOpen}
                            autoHideDuration={6000}
                            onClose={() => setSnackbarOpen(false)}
                        >
                            <Alert
                                onClose={() => setSnackbarOpen(false)}
                                severity={snackbarContent.type}
                                variant="filled"
                                sx={{ width: "100%" }}
                            >
                                {snackbarContent.message}
                            </Alert>
                        </Snackbar>
                    </form>
                )}
            </div>
        </div>
    );
}
